import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Briefcase, Filter, ExternalLink, Star, Flame, RefreshCw,
  MessageCircle, ChevronLeft, ChevronRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 12;

const TYPE_LABELS = { cherche: "Je cherche", propose: "Je propose", opportunite: "Opportunité" };
const TYPE_TOOLTIPS = {
  cherche: "Vous cherchez un prestataire, partenaire ou client spécifique",
  propose: "Vous proposez une offre ou un service",
  opportunite: "Vous partagez une opportunité (job, mission, collaboration)",
};

function TypeBadge({ type }) {
  const cls = type === "cherche" ? "cbm-badge-type-cherche" : type === "propose" ? "cbm-badge-type-propose" : "cbm-badge-type-opportunite";
  return (
    <div className="cbm-tooltip inline-flex">
      <span className={`cbm-badge ${cls}`}>{TYPE_LABELS[type] || type}</span>
      <div className="cbm-tooltip-content">{TYPE_TOOLTIPS[type]}</div>
    </div>
  );
}

function Avatar({ profile, size = 32 }) {
  const initials = (profile?.nom_prenom || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (profile?.photo) return <img src={`${API}${profile.photo}`} alt={profile.nom_prenom} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
  return <div className="cbm-avatar flex-shrink-0 text-gold font-outfit font-semibold" style={{ width: size, height: size, fontSize: size * 0.38, background: "linear-gradient(135deg, #132040, #0D1B3E)" }}>{initials}</div>;
}

function OppCard({ opp, index }) {
  const navigate = useNavigate();
  return (
    <div className={`cbm-card p-5 flex flex-col gap-3 animate-fade-in-up-delay-${Math.min(index % 6 + 1, 6)}`} data-testid={`opp-card-${opp._id}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <TypeBadge type={opp.type} />
        <span className={`cbm-badge ${opp.statut === "ouvert" ? "cbm-badge-open" : "cbm-badge-closed"}`}>{opp.statut === "ouvert" ? "Ouvert" : "Clôturé"}</span>
        {opp.is_featured && <span className="cbm-badge cbm-badge-featured"><Star size={9} className="mr-0.5" />À la une</span>}
        {opp.is_priority && <span className="cbm-badge cbm-badge-priority"><Flame size={9} className="mr-0.5" />Prioritaire</span>}
      </div>
      <h3
        className="font-outfit text-white leading-snug"
        style={{ fontSize: "1.1875rem", fontWeight: 700, letterSpacing: "-0.01em" }}
      >
        {opp.titre}
      </h3>
      <div className="flex items-center gap-2 flex-wrap" style={{ color: "#AFC3E0", fontSize: "0.8125rem", fontWeight: 500 }}>
        {opp.secteur && <span>{opp.secteur}</span>}
        {opp.secteur && opp.ville && <span style={{ color: "#6A7BA3" }}>·</span>}
        {opp.ville && <span>{opp.ville}</span>}
      </div>
      {opp.description && (
        <p
          className="line-clamp-2"
          style={{ color: "#CFD9EC", fontSize: "0.9375rem", lineHeight: 1.55 }}
        >
          {opp.description}
        </p>
      )}
      {opp.author && (
        <button
          data-testid={`view-author-profile-${opp._id}`}
          onClick={() => navigate(`/profil/${opp.author.id}`)}
          className="flex items-center gap-2.5 pt-3 mt-auto border-t cbm-divider w-full text-left transition-colors hover:bg-white/[0.02] -mx-5 px-5 -mb-5 pb-3 rounded-b-xl group"
        >
          <Avatar profile={opp.author} size={32} />
          <div className="flex-1 min-w-0">
            <p className="text-white truncate" style={{ fontSize: "0.875rem", fontWeight: 600 }}>{opp.author.nom_prenom}</p>
            {(opp.author.is_ambassador || opp.author.is_top_connector) && (
              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                {opp.author.is_ambassador && <span className="cbm-badge cbm-badge-ambassador" data-testid={`opp-author-ambassador-${opp._id}`}>◆ Ambassadeur</span>}
                {opp.author.is_top_connector && <span className="cbm-badge cbm-badge-top-connector" data-testid={`opp-author-top-connector-${opp._id}`}>⚡ Top connecteur</span>}
              </div>
            )}
            {opp.author.entreprise && (
              <p className="truncate" style={{ color: "#AFC3E0", fontSize: "0.8125rem" }}>{opp.author.entreprise}</p>
            )}
          </div>
          <span className="text-cbm-muted group-hover:text-gold transition-colors flex-shrink-0">
            <ExternalLink size={14} />
          </span>
        </button>
      )}
    </div>
  );
}

function Pagination({ page, pages, total, onChange }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-8 animate-fade-in">
      <button data-testid="prev-page-opp" disabled={page === 1} onClick={() => onChange(page - 1)}
        className="cbm-btn-ghost text-sm border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed py-2 px-3">
        <ChevronLeft size={16} />
      </button>
      <div className="flex items-center gap-1">
        {Array.from({ length: pages }, (_, i) => i + 1).filter(p => Math.abs(p - page) <= 2 || p === 1 || p === pages).map((p, idx, arr) => (
          <span key={p}>
            {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-cbm-muted text-sm px-1">...</span>}
            <button onClick={() => onChange(p)} className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${p === page ? "bg-gold text-navy font-semibold" : "text-cbm-secondary hover:bg-white/5"}`}>{p}</button>
          </span>
        ))}
      </div>
      <button data-testid="next-page-opp" disabled={page === pages} onClick={() => onChange(page + 1)}
        className="cbm-btn-ghost text-sm border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed py-2 px-3">
        <ChevronRight size={16} />
      </button>
      <span className="text-cbm-muted text-xs">{total} annonces</span>
    </div>
  );
}

export default function Opportunites() {
  const [opps, setOpps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [secteur, setSecteur] = useState("");
  const [ville, setVille] = useState("");
  const [secteurs, setSecteurs] = useState([]);
  const [villes, setVilles] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (type) params.type = type;
      if (secteur) params.secteur = secteur;
      if (ville) params.ville = ville;
      const { data } = await axios.get(`${API}/api/opportunities`, { params, withCredentials: true });
      setOpps(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [type, secteur, ville, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/api/filters/secteurs`, { withCredentials: true }).then(({ data }) => setSecteurs(data)).catch(() => {});
    axios.get(`${API}/api/filters/villes`, { withCredentials: true }).then(({ data }) => setVilles(data)).catch(() => {});
  }, []);

  const clearFilters = () => { setType(""); setSecteur(""); setVille(""); setPage(1); };
  const handlePageChange = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 animate-fade-in-up">
        <div>
          <h1 className="cbm-section-title">Opportunités</h1>
          <p className="text-cbm-muted text-sm mt-1">{total} annonce{total > 1 ? "s" : ""}</p>
        </div>
        <button data-testid="post-opportunity-button" className="cbm-btn-primary text-sm" onClick={() => navigate("/poster-annonce")}>
          <MessageCircle size={15} /> Poster une annonce
        </button>
      </div>

      <div className="cbm-filter-bar mb-6 animate-fade-in-up-delay-1">
        <div className="flex flex-col sm:flex-row gap-3">
          <select data-testid="filter-type" className="cbm-input sm:w-48" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
            <option value="">Tous les types</option>
            <option value="cherche">Je cherche</option>
            <option value="propose">Je propose</option>
            <option value="opportunite">Opportunité</option>
          </select>
          <select data-testid="filter-secteur-opp" className="cbm-input sm:w-48" value={secteur} onChange={(e) => { setSecteur(e.target.value); setPage(1); }}>
            <option value="">Tous les secteurs</option>
            {secteurs.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-ville-opp" className="cbm-input sm:w-40" value={ville} onChange={(e) => { setVille(e.target.value); setPage(1); }}>
            <option value="">Toutes les villes</option>
            {villes.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          {(type || secteur || ville) && (
            <button className="cbm-btn-ghost text-sm border border-white/10" onClick={clearFilters} data-testid="clear-opp-filters">
              <RefreshCw size={14} /> Réinitialiser
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cbm-card p-5 space-y-3">
              <div className="cbm-skeleton h-5 w-24 rounded-full" /><div className="cbm-skeleton h-4 w-full" /><div className="cbm-skeleton h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : opps.length === 0 ? (
        <div className="text-center py-20 animate-fade-in">
          <Briefcase size={40} className="text-cbm-muted mx-auto mb-3" />
          <p className="text-cbm-secondary font-outfit text-lg">Aucune annonce trouvée</p>
          <button className="cbm-btn-primary mt-4 text-sm" onClick={() => navigate("/poster-annonce")} data-testid="post-first-opp">Poster une annonce</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {opps.map((o, i) => <OppCard key={o._id} opp={o} index={i} />)}
          </div>
          <Pagination page={page} pages={pages} total={total} onChange={handlePageChange} />
        </>
      )}
    </div>
  );
}

